import type { TFunction } from 'i18next';
import { Locale } from '../../i18n/config';
import { isGenAiLogomakerAvailable } from '../../utils/featureAvailability';

export type BusinessToolKey =
    | 'businessNameGenerator'
    | 'brandKit'
    | 'wixWebsites'
    | 'socialMediaTemplates'
    | 'design'
    | 'logomaker'
    | 'swyftFilings'
    | 'googleWorkspace';

interface BusinessTool {
    description: string;
    url: string;
    image: {
        src: string;
        altText: string;
    };
}

export function getLaunchMyBusinessToolsConfig(
    businessTools: BusinessToolKey[],
    t: TFunction,
    locale: Locale,
): (BusinessTool & { businessToolKey: BusinessToolKey })[] {
    const launchMyBusinessToolsConfig: Record<BusinessToolKey, BusinessTool> = {
        businessNameGenerator: {
            description: t(
                'Vista Logos > Launch My Business Tools > Business Name Generator.productTileHeader',
            ),
            url: t(
                'Vista Logos > Launch My Business Tools > Business Name Generator.url.contentfulUrl',
            ),
            image: {
                src: t(
                    'Vista Logos > Launch My Business Tools > Business Name Generator.productImage.src',
                ),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Business Name Generator.productImage.altText',
                ),
            },
        },
        brandKit: {
            description: t('Vista Logos > Launch My Business Tools > Brand Kit.productTileHeader'),
            url: t('Vista Logos > Launch My Business Tools > Brand Kit.url.contentfulUrl'),
            image: {
                src: t('Vista Logos > Launch My Business Tools > Brand Kit.productImage.src'),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Brand Kit.productImage.altText',
                ),
            },
        },
        wixWebsites: {
            description: t(
                'Vista Logos > Launch My Business Tools > Vista x Wix Websites.productTileHeader',
            ),
            url: t(
                'Vista Logos > Launch My Business Tools > Vista x Wix Websites.url.contentfulUrl',
            ),
            image: {
                src: t(
                    'Vista Logos > Launch My Business Tools > Vista x Wix Websites.productImage.src',
                ),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Vista x Wix Websites.productImage.altText',
                ),
            },
        },
        socialMediaTemplates: {
            description: t(
                'Vista Logos > Launch My Business Tools > Social Media Templates.productTileHeader',
            ),
            url: t(
                'Vista Logos > Launch My Business Tools > Social Media Templates.url.contentfulUrl',
            ),
            image: {
                src: t(
                    'Vista Logos > Launch My Business Tools > Social Media Templates.productImage.src',
                ),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Social Media Templates.productImage.altText',
                ),
            },
        },
        design: {
            description: t(
                'Vista Logos > Launch My Business Tools > Design Services.productTileHeader',
            ),
            url: t('Vista Logos > Launch My Business Tools > Design Services.url.contentfulUrl'),
            image: {
                src: t('Vista Logos > Launch My Business Tools > Design Services.productImage.src'),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Design Services.productImage.altText',
                ),
            },
        },
        logomaker: {
            description: t('Vista Logos > Launch My Business Tools > Logomaker.productTileHeader'),
            url: t('Vista Logos > Launch My Business Tools > Logomaker.url.contentfulUrl'),
            image: {
                src: t('Vista Logos > Launch My Business Tools > Logomaker.productImage.src'),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Logomaker.productImage.altText',
                ),
            },
        },
        swyftFilings: {
            description: t(
                'Vista Logos > Launch My Business Tools > Swyft Filings.productTileHeader',
            ),
            url: t('Vista Logos > Launch My Business Tools > Swyft Filings.url.contentfulUrl'),
            image: {
                src: t('Vista Logos > Launch My Business Tools > Swyft Filings.productImage.src'),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Swyft Filings.productImage.altText',
                ),
            },
        },
        googleWorkspace: {
            description: t(
                'Vista Logos > Launch My Business Tools > Google WorkSpace.productTileHeader',
            ),
            url: t('Vista Logos > Launch My Business Tools > Google WorkSpace.url.contentfulUrl'),
            image: {
                src: t(
                    'Vista Logos > Launch My Business Tools > Google WorkSpace.productImage.src',
                ),
                altText: t(
                    'Vista Logos > Launch My Business Tools > Google WorkSpace.productImage.altText',
                ),
            },
        },
    };

    if (isGenAiLogomakerAvailable(locale)) {
        launchMyBusinessToolsConfig.logomaker.description = 'Create a free, AI logo';
    }

    const config = businessTools.map((businessToolKey) => ({
        businessToolKey,
        ...launchMyBusinessToolsConfig[businessToolKey],
    }));

    return config;
}
